/**
 * Arc — great-circle path between two lat/lon points on a sphere.
 *
 * Renders a tube along the slerp interpolation of the two endpoints lifted
 * above the surface by a height proportional to the chord length. An animated
 * dash offset in the fragment shader gives the impression of packets flowing.
 *
 * This component is shaped for instancing: a single Arc here is the per-flow
 * primitive; ArcPass in the rendering engine batches many arcs into one draw.
 */

import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import {
  CatmullRomCurve3,
  Color,
  type Mesh,
  ShaderMaterial,
  TubeGeometry,
  Vector3,
} from 'three';

import { latLonToVec3 } from './coords.js';

export interface LatLon {
  readonly lat: number;
  readonly lon: number;
}

export interface ArcProps {
  readonly from: LatLon;
  readonly to: LatLon;
  /** Globe radius the arc sits on. */
  readonly radius?: number;
  /** Number of segments along the arc. Default 64. */
  readonly segments?: number;
  /** Tube radius (visual thickness). Default 0.004 * radius. */
  readonly thickness?: number;
  /** Color of the flowing packet stream. */
  readonly color?: string | number;
  /** Dash flow speed (cycles/sec). Default 1.5. */
  readonly speed?: number;
  /** Number of visible dashes along the arc. Default 6. */
  readonly dashCount?: number;
  /** Live utilization 0..1 — scales brightness and flow speed. Default 0.5. */
  readonly intensity?: number;
}

// Endpoints anchor at the PoP marker height so arcs visibly start/end *on* the
// PoPs; the surface lift matches Pops' SURFACE_LIFT.
const ANCHOR = 1.012;
// Constant peak height above the anchor — every arc rises to the same level
// regardless of length, so short hops are elevated like long ones and arcs
// leaving the same PoP share an identical take-off (no vertical stacking).
const BULGE = 0.12;

function buildGreatCircle(
  from: LatLon,
  to: LatLon,
  radius: number,
  segments: number,
): Vector3[] {
  // Unit endpoints for the slerp; height is applied per point below.
  const a = latLonToVec3(from.lat, from.lon, 1);
  const b = latLonToVec3(to.lat, to.lon, 1);
  const angle = a.angleTo(b);
  const sinAngle = Math.sin(angle);
  const baseR = radius * ANCHOR;
  // Constant elevation: same peak for every arc (see BULGE).
  const bulge = radius * BULGE;
  const points: Vector3[] = [];
  if (sinAngle < 1e-6) {
    points.push(a.clone().multiplyScalar(baseR), b.clone().multiplyScalar(baseR));
    return points;
  }
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const w1 = Math.sin((1 - t) * angle) / sinAngle;
    const w2 = Math.sin(t * angle) / sinAngle;
    // Great-circle direction at t, then height = anchor + a sine bulge that is
    // 0 at both PoPs and peaks at the midpoint — a true projected arc.
    const h = baseR + bulge * Math.sin(Math.PI * t);
    const p = new Vector3()
      .copy(a)
      .multiplyScalar(w1)
      .addScaledVector(b, w2)
      .normalize()
      .multiplyScalar(h);
    points.push(p);
  }
  return points;
}

const VERT = /* glsl */ `
  varying float vT;
  void main() {
    vT = uv.x;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG = /* glsl */ `
  precision highp float;
  uniform vec3 uColor;
  uniform float uTime;
  uniform float uSpeed;
  uniform float uDashCount;
  uniform float uIntensity;
  varying float vT;
  void main() {
    float phase = fract(vT * uDashCount - uTime * uSpeed);
    float head = smoothstep(0.45, 0.5, phase) * (1.0 - smoothstep(0.5, 0.55, phase));
    float tail = pow(phase, 3.0);
    // A faint base line keeps the route legible when idle; the moving packet
    // head + tail brighten with live utilization.
    float base = 0.06;
    float alpha = clamp(base + (head * 1.4 + tail * 0.25) * (0.35 + uIntensity), 0.0, 1.0);
    gl_FragColor = vec4(uColor, alpha);
  }
`;

export function Arc({
  from,
  to,
  radius = 1,
  segments = 64,
  thickness,
  color = 0x6cf0ff,
  speed = 1.5,
  dashCount = 6,
  intensity = 0.5,
}: ArcProps): JSX.Element {
  const meshRef = useRef<Mesh>(null);

  const geometry = useMemo(() => {
    const pts = buildGreatCircle(from, to, radius, segments);
    const curve = new CatmullRomCurve3(pts);
    return new TubeGeometry(curve, segments, thickness ?? 0.004 * radius, 8, false);
  }, [from, to, radius, segments, thickness]);

  const material = useMemo(
    () =>
      new ShaderMaterial({
        vertexShader: VERT,
        fragmentShader: FRAG,
        uniforms: {
          uColor: { value: new Color(color) },
          uTime: { value: 0 },
          uSpeed: { value: speed },
          uDashCount: { value: dashCount },
          uIntensity: { value: 0.5 },
        },
        transparent: true,
        depthWrite: false,
      }),
    // `intensity` is intentionally not a dependency: rebuilding the material
    // would reset `uTime` and stutter the flow. It's pushed to the uniform in
    // the effect below (which also sets the real initial value on mount).
    [color, speed, dashCount],
  );

  useEffect(() => {
    material.uniforms.uIntensity!.value = intensity;
  }, [material, intensity]);

  useFrame((_, dt) => {
    // Busier links flow faster: advance time scaled by live utilization.
    material.uniforms.uTime!.value += dt * (0.5 + intensity);
  });

  return <mesh ref={meshRef} geometry={geometry} material={material} />;
}
